import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button, Callout, Checkbox, DropdownMenu, EmptyState, Modal, Spinner, Tab, TabGroup, Table, Tag, TextField,
  type TableColumn,
} from '@humanforce/mango-ui-react';
import { PageTitle } from './PageTitle';
import { ToastContainer, useToast } from './ToastContainer';
import {
  createUser, deactivateUser, getAssignableRoles, listUsers, reactivateUser, setUserPassword, updateUserProfile,
  updateUserRoles, UsersAccessApiError,
  type AssignableRole, type TenantUser, type TenantUserListPage, type TenantUserSortField,
} from './usersAccessApi';
import { hasErrors, validateUserDetails, type UserDetailsDraft, type UserDetailsFieldErrors } from './usersAccessValidation';

const PAGE_SIZE = 25;

const blankDraft = (): UserDetailsDraft => ({ firstName: '', lastName: '', email: '', password: '', confirmPassword: '' });
const draftFrom = (user: TenantUser): UserDetailsDraft => ({ firstName: user.firstName, lastName: user.lastName, email: user.email, password: '', confirmPassword: '' });

const statusLabel = (status: TenantUser['status']): string => (status === 'active' ? 'Active' : 'Deactivated');
const assignableRoleNames = (roles: string[], assignable: AssignableRole[]): string[] => {
  const allowed = new Set(assignable.map((role) => role.name));
  return roles.filter((role) => allowed.has(role));
};
const roleDisplayName = (roleName: string, assignable: AssignableRole[]): string =>
  assignable.find((role) => role.name === roleName)?.displayName ?? roleName;

/** PRR-316 tenant Users & Access screen: server-paged/searched/sorted grid, two-tab Add/Edit, deactivate confirm, reactivate, and password reset. */
export const UsersAccessScreen = () => {
  const { addToast, removeToast, toasts } = useToast();

  // --- list state ---------------------------------------------------------
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<TenantUserSortField>('lastName');
  const [sortDescending, setSortDescending] = useState(false);
  const [first, setFirst] = useState(0);
  const [data, setData] = useState<TenantUserListPage>();
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');
  const requestId = useRef(0);

  useEffect(() => {
    const handle = window.setTimeout(() => { setSearch(searchInput.trim()); setFirst(0); }, 250);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  const load = useCallback((signal?: AbortSignal) => {
    const current = ++requestId.current;
    setLoading(true);
    setListError('');
    const page = Math.floor(first / PAGE_SIZE) + 1;
    listUsers({ search: search || undefined, sortBy, sortDescending, page, pageSize: PAGE_SIZE }, signal)
      .then((result) => { if (requestId.current === current) setData(result); })
      .catch((error: unknown) => {
        if (requestId.current !== current || (error instanceof DOMException && error.name === 'AbortError')) return;
        setListError(error instanceof UsersAccessApiError ? error.message : 'Users could not be loaded.');
      })
      .finally(() => { if (requestId.current === current) setLoading(false); });
  }, [search, sortBy, sortDescending, first]);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  // --- assignable roles (R-010: server is the canonical source) -----------
  const [assignableRoles, setAssignableRoles] = useState<AssignableRole[]>([]);
  const [rolesError, setRolesError] = useState('');
  const loadAssignableRoles = useCallback(() => {
    setRolesError('');
    getAssignableRoles()
      .then(setAssignableRoles)
      .catch(() => setRolesError('Assignable roles could not be loaded.'));
  }, []);
  useEffect(() => { loadAssignableRoles(); }, [loadAssignableRoles]);

  // --- Add/Edit dialog ------------------------------------------------------
  const [dialogMode, setDialogMode] = useState<'create' | 'edit' | null>(null);
  const [dialogTab, setDialogTab] = useState<'details' | 'roles'>('details');
  const [editingUser, setEditingUser] = useState<TenantUser>();
  const [draft, setDraft] = useState<UserDetailsDraft>(blankDraft());
  const [draftRoles, setDraftRoles] = useState<string[]>([]);
  const [fieldErrors, setFieldErrors] = useState<UserDetailsFieldErrors>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const openCreate = () => {
    setDialogMode('create'); setDialogTab('details'); setEditingUser(undefined);
    setDraft(blankDraft()); setDraftRoles([]); setFieldErrors({}); setSaveError('');
  };
  const openEdit = (user: TenantUser) => {
    setDialogMode('edit'); setDialogTab('details'); setEditingUser(user);
    setDraft(draftFrom(user)); setDraftRoles(assignableRoleNames(user.roles, assignableRoles)); setFieldErrors({}); setSaveError('');
  };
  const closeDialog = () => { if (!saving) setDialogMode(null); };

  const toggleDraftRole = (roleName: string, checked: boolean) =>
    setDraftRoles((current) => (checked ? [...current, roleName] : current.filter((role) => role !== roleName)));

  const saveDialog = async () => {
    const errors = validateUserDetails(draft, { requirePassword: dialogMode === 'create' });
    setFieldErrors(errors);
    if (hasErrors(errors)) { setDialogTab('details'); return; }

    setSaving(true);
    setSaveError('');
    try {
      if (dialogMode === 'create') {
        const created = await createUser({
          firstName: draft.firstName.trim(), lastName: draft.lastName.trim(), email: draft.email.trim(),
          password: draft.password, roles: draftRoles,
        });
        addToast(`User created: ${created.email}`, 'positive');
      } else if (editingUser) {
        await updateUserProfile(editingUser.id, {
          firstName: draft.firstName.trim(), lastName: draft.lastName.trim(), email: draft.email.trim(),
        });
        const currentAssignableRoles = assignableRoleNames(editingUser.roles, assignableRoles);
        if (JSON.stringify([...draftRoles].sort()) !== JSON.stringify([...currentAssignableRoles].sort()))
          await updateUserRoles(editingUser.id, draftRoles);
        addToast(`User updated: ${draft.email.trim()}`, 'positive');
      }
      setDialogMode(null);
      load();
    } catch (error) {
      setSaveError(error instanceof UsersAccessApiError ? error.message : 'The user could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  // --- deactivate confirmation ----------------------------------------------
  const [deactivateTarget, setDeactivateTarget] = useState<TenantUser>();
  const [deactivateSaving, setDeactivateSaving] = useState(false);
  const [deactivateError, setDeactivateError] = useState('');

  const confirmDeactivate = async () => {
    if (!deactivateTarget) return;
    setDeactivateSaving(true);
    setDeactivateError('');
    try {
      await deactivateUser(deactivateTarget.id);
      addToast(`User deactivated: ${deactivateTarget.email}`, 'positive');
      setDeactivateTarget(undefined);
      load();
    } catch (error) {
      setDeactivateError(error instanceof UsersAccessApiError ? error.message : 'The user could not be deactivated.');
    } finally {
      setDeactivateSaving(false);
    }
  };

  const reactivate = async (user: TenantUser) => {
    try {
      await reactivateUser(user.id);
      addToast(`User reactivated: ${user.email}`, 'positive');
      load();
    } catch (error) {
      addToast(error instanceof UsersAccessApiError ? error.message : 'The user could not be reactivated.', 'critical', { sticky: true });
    }
  };

  // --- set password dialog ---------------------------------------------------
  const [passwordTarget, setPasswordTarget] = useState<TenantUser>();
  const [passwordValue, setPasswordValue] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  const savePassword = async () => {
    if (!passwordTarget) return;
    if (!passwordValue.trim()) { setPasswordError('Password is required.'); return; }
    if (!passwordConfirmation.trim()) { setPasswordError('Confirm password is required.'); return; }
    if (passwordValue !== passwordConfirmation) { setPasswordError('Passwords do not match.'); return; }
    setPasswordSaving(true);
    setPasswordError('');
    try {
      await setUserPassword(passwordTarget.id, passwordValue);
      addToast(`Password set for ${passwordTarget.email}`, 'positive');
      setPasswordTarget(undefined);
      setPasswordValue('');
    } catch (error) {
      setPasswordError(error instanceof UsersAccessApiError ? error.message : 'The password could not be set.');
    } finally {
      setPasswordSaving(false);
    }
  };

  // Recomputed every render (cheap column defs) rather than memoized: several row actions
  // below close over `load`/`reactivate`, whose identities change with search/sort/page
  // state, so memoizing this array would capture stale closures.
  const columns: TableColumn[] = [
    { field: 'firstName', header: 'First name', sortable: true },
    { field: 'lastName', header: 'Last name', sortable: true },
    { field: 'email', header: 'Email', sortable: true },
    { field: 'status', header: 'Status', sortable: true, body: (row: TenantUser) => statusLabel(row.status) },
    {
      field: 'roles',
      header: 'Roles',
      body: (row: TenantUser) => row.roles.length > 0 ? (
        <div className="mgo-cluster mgo-gap-xs">
          {row.roles.map((role) => <Tag key={role} label={roleDisplayName(role, assignableRoles)} tone="blue" testId={`user-role-${row.id}-${role}`} />)}
        </div>
      ) : '—',
    },
    {
      field: 'actions', header: '', alignFrozen: 'right',
      body: (row: TenantUser) => (
        <DropdownMenu
          trigger={<Button label="Actions" variant="tertiary" size="small" testId={`user-actions-${row.id}`} />}
          items={[
            { label: 'Edit', icon: 'edit', onClick: () => openEdit(row) },
            { label: 'Set password', icon: 'lock', onClick: () => { setPasswordTarget(row); setPasswordValue(''); setPasswordConfirmation(''); setPasswordError(''); } },
            { separator: true },
            row.status === 'active'
              ? { label: 'Deactivate', icon: 'delete', variant: 'destructive', onClick: () => { setDeactivateTarget(row); setDeactivateError(''); } }
              : { label: 'Reactivate', icon: 'refresh', onClick: () => void reactivate(row) },
          ]}
        />
      ),
    },
  ];

  const sortOrder = sortDescending ? -1 : 1;

  return (
    <div className="mgo-stack mgo-gap-l" data-testid="users-access-screen">
      <PageTitle
        title="Users & Access"
        subtitle="Create and manage tenant users, their profiles and their assigned roles."
        actions={<Button label="Add User" icon="add" onClick={openCreate} testId="add-user-button" />}
      />

      {rolesError && <Callout tone="caution">{rolesError}</Callout>}

      <TextField
        label="Search"
        hideLabel
        placeholder="Search by first name, last name or email"
        leadingIcon="search"
        clearable
        value={searchInput}
        onChange={(event) => setSearchInput(event.target.value)}
        onClear={() => setSearchInput('')}
        testId="users-access-search"
      />

      {listError ? (
        <Callout tone="critical" title="Users could not be loaded">
          {listError}
          <Button label="Try again" variant="secondary" size="small" onClick={() => load()} />
        </Callout>
      ) : !data && loading ? (
        <div role="status" className="mgo-cluster mgo-gap-s"><Spinner /><span>Loading users…</span></div>
      ) : !loading && data && data.totalCount === 0 && !search ? (
        <EmptyState variant="no-data" title="No users yet" description="Use Add User to create the first tenant user." actions={[<Button key="add" label="Add User" onClick={openCreate} />]} />
      ) : (
        <Table
          testId="users-access-table"
          value={data?.items ?? []}
          columns={columns}
          loading={loading}
          lazy
          paginated
          totalRecords={data?.totalCount ?? 0}
          first={first}
          rowsPerPage={PAGE_SIZE}
          sortField={sortBy}
          sortOrder={sortOrder}
          emptyMessage="No users match this search."
          onLazyLoad={(event: { first: number; sortField?: string; sortOrder?: number }) => {
            setFirst(event.first);
            if (event.sortField) {
              setSortBy(event.sortField as TenantUserSortField);
              setSortDescending(event.sortOrder === -1);
            }
          }}
        />
      )}

      {dialogMode && (
        <Modal
          visible
          header={dialogMode === 'create' ? 'Add User' : `Edit ${editingUser?.email}`}
          onHide={closeDialog}
          testId="user-dialog"
          footer={
            <>
              <Button label="Cancel" variant="secondary" onClick={closeDialog} disabled={saving} />
              <Button label="Save" onClick={() => void saveDialog()} loading={saving} testId="save-user-button" />
            </>
          }
        >
          <div className="mgo-stack mgo-gap-m">
            <TabGroup value={dialogTab} onValueChange={(value) => setDialogTab(value as 'details' | 'roles')}>
              <Tab value="details" label="User Details">
                <div className="mgo-stack mgo-gap-m">
                  <TextField
                    label="First name" required value={draft.firstName}
                    status={fieldErrors.firstName ? 'critical' : undefined} statusText={fieldErrors.firstName}
                    onChange={(event) => setDraft((current) => ({ ...current, firstName: event.target.value }))}
                  />
                  <TextField
                    label="Last name" required value={draft.lastName}
                    status={fieldErrors.lastName ? 'critical' : undefined} statusText={fieldErrors.lastName}
                    onChange={(event) => setDraft((current) => ({ ...current, lastName: event.target.value }))}
                  />
                  <TextField
                    label="Email" required value={draft.email}
                    status={fieldErrors.email ? 'critical' : undefined} statusText={fieldErrors.email}
                    onChange={(event) => setDraft((current) => ({ ...current, email: event.target.value }))}
                  />
                  {dialogMode === 'create' && (
                    <>
                      <TextField
                        label="Initial password" required type="password" value={draft.password}
                        status={fieldErrors.password ? 'critical' : undefined} statusText={fieldErrors.password}
                        onChange={(event) => setDraft((current) => ({ ...current, password: event.target.value }))}
                      />
                      <TextField
                        label="Confirm password" required type="password" value={draft.confirmPassword}
                        status={fieldErrors.confirmPassword ? 'critical' : undefined} statusText={fieldErrors.confirmPassword}
                        onChange={(event) => setDraft((current) => ({ ...current, confirmPassword: event.target.value }))}
                      />
                    </>
                  )}
                  {saveError && <Callout tone="critical">{saveError}</Callout>}
                </div>
              </Tab>
              <Tab value="roles" label="Roles">
                <div className="mgo-stack mgo-gap-s">
                  {rolesError && (
                    <Callout tone="critical" title="Roles could not be loaded">
                      {rolesError}
                      <Button label="Try again" variant="secondary" size="small" onClick={loadAssignableRoles} />
                    </Callout>
                  )}
                  {assignableRoles.map((role) => (
                    <Checkbox
                      key={role.name}
                      id={`role-${role.name}`}
                      label={role.displayName}
                      checked={draftRoles.includes(role.name)}
                      onChange={(checked: boolean) => toggleDraftRole(role.name, checked)}
                    />
                  ))}
                  {saveError && <Callout tone="critical">{saveError}</Callout>}
                </div>
              </Tab>
            </TabGroup>
          </div>
        </Modal>
      )}

      {deactivateTarget && (
        <Modal
          visible
          header={`Deactivate ${deactivateTarget.email}?`}
          onHide={() => setDeactivateTarget(undefined)}
          testId="deactivate-dialog"
          footer={<><Button label="Cancel" variant="secondary" onClick={() => setDeactivateTarget(undefined)} disabled={deactivateSaving} /><Button label="Deactivate user" onClick={() => void confirmDeactivate()} loading={deactivateSaving} /></>}
        >
          <div className="mgo-stack mgo-gap-m">
            <p>
              <span className="mgo-weight-strong">{deactivateTarget.firstName} {deactivateTarget.lastName}</span> ({deactivateTarget.email}) will
              no longer be able to sign in or continue an existing session. Their record is retained and can be reactivated later.
            </p>
            {deactivateError && <Callout tone="critical">{deactivateError}</Callout>}
          </div>
        </Modal>
      )}

      {passwordTarget && (
        <Modal
          visible
          header={`Set password for ${passwordTarget.email}`}
          onHide={() => setPasswordTarget(undefined)}
          testId="set-password-dialog"
          footer={
            <>
              <Button label="Cancel" variant="secondary" onClick={() => setPasswordTarget(undefined)} disabled={passwordSaving} />
              <Button label="Set password" onClick={() => void savePassword()} loading={passwordSaving} />
            </>
          }
        >
          <div className="mgo-stack mgo-gap-m">
            <TextField
              label="New password" type="password" required value={passwordValue}
              status={passwordError ? 'critical' : undefined} statusText={passwordError}
              onChange={(event) => setPasswordValue(event.target.value)}
            />
            <TextField
              label="Confirm password" type="password" required value={passwordConfirmation}
              status={passwordError ? 'critical' : undefined} statusText={passwordError}
              onChange={(event) => setPasswordConfirmation(event.target.value)}
            />
          </div>
        </Modal>
      )}

      <ToastContainer toasts={toasts} onClose={removeToast} />
    </div>
  );
};
